import { useEffect, useState, JSX } from 'react';
import {
    RaRecord,
    useRecordContext
} from 'react-admin';
import {
    Box,
    Grid,
    Button,
    IconButton
} from '@mui/material'
import {
    Refresh
} from '@mui/icons-material';
import { Operator} from "../../../../types/entities";
import {
    fetchOperatorConnection,
    operatorRequestHeaders,
    OperatorConnection
} from '../../../../providers/operatorConnection';
const statusStyles = {
    padding: '0 10px'
}

/**
 * What the Operator answers a consumer action with.
 *
 * Stage 5 of the queue screens. Start, stop and restart were `GET`s answering
 * `{"message": "<a JSON string>"}` with a sentence in it; they are `POST`s answering plain JSON
 * carrying the state they left behind. A refusal is a 503 with the same fields under `detail`,
 * which is why the state is read from either.
 */
type ConsumerAction = {
    action?: string,
    status?: string,
    message?: string,
    connected?: boolean,
    consuming?: boolean,
    draining?: boolean,
};

export const OperatorStatus: () => JSX.Element = (): JSX.Element => {
    const record: Operator = useRecordContext();
    const [status, setStatus] = useState<string>('Inactive');
    const [loading, setLoading] = useState<boolean>(true);
    const [problem, setProblem] = useState<string>('');

    /**
     * HQAPI-76. The host and the credential used to come off the Operator record this screen
     * already had, because `GET /hq_api/operators` served `token` - the Operator's own standing
     * secret, which opens every route on it. That field is not published any more, so the
     * connection is asked for here and the ticket it answers with lasts about a minute.
     *
     * Asked for on each action rather than once on mount: these buttons are minutes apart in
     * practice and a ticket held that long would have lapsed.
     */
    async function operatorConsumerAction(action: string): Promise<void> {
        setLoading(true);
        setProblem('');
        try {
            const connection: OperatorConnection = await fetchOperatorConnection(record.id);
            const response: Response = await fetch(new Request(
                connection.host + '/system/status/' + action,
                {
                    // A POST because these mutate. As GETs, anything that walks a URL - a
                    // prefetch, a crawler, an admin's history - could detach a live consumer.
                    method: 'POST',
                    headers: operatorRequestHeaders(connection),
                }
            ));
            const body: any = await response.json();
            // A refusal carries the same fields, so the screen can still show where the Operator
            // ended up rather than leaving the last reading on display as though nothing happened.
            const result: ConsumerAction = response.ok ? body : (body?.detail ?? {});

            setStatus(result.consuming === true ? 'Online' : 'Offline');

            if (!response.ok) {
                setProblem(result.message ?? 'The Operator refused that.');
            }
        } catch (unreachable: any) {
            // **Shown, not only logged.** This used to `console.log` and leave the panel reading
            // whatever it read last, so an Operator that could not be reached at all looked
            // exactly like one that answered.
            setProblem(unreachable?.message ?? 'The Operator could not be reached.');
        } finally {
            setLoading(false);
        }
    }

    async function handleRestartOperator(): Promise<void> {
        await operatorConsumerAction('restart');
    }

    async function handleStopOperator(): Promise<void>  {
        await operatorConsumerAction('stop');
    }

    async function handleStartOperator(): Promise<void> {
        await operatorConsumerAction('start');
    }

    /**
     * Read the status without changing it.
     *
     * **Still the old shape**, a JSON string inside a `message` field. `GET /system/status/` is a
     * read, it has a second caller in the workflow testing tool's Operator tile, and converting it
     * was not part of this stage.
     */
    async function refreshOperatorStatus(): Promise<void> {
        setLoading(true);
        setProblem('');
        try {
            const connection: OperatorConnection = await fetchOperatorConnection(record.id);
            const response: Response = await fetch(new Request(connection.host + '/system/status/', {
                method: 'GET',
                headers: operatorRequestHeaders(connection),
            }));
            const body: any = await response.json();
            const reading: any = typeof body?.message === 'string' ? JSON.parse(body.message) : {};

            setStatus(reading?.response === 'Online' ? 'Online' : 'Offline');
        } catch (unreachable: any) {
            setProblem(unreachable?.message ?? 'The Operator could not be reached.');
        } finally {
            setLoading(false);
        }
    }

    useEffect( (): void => {
        ( async (): Promise<void> => {
                await refreshOperatorStatus();
            }
        )();
    }, [record] );

    return (
        <Grid container spacing={2} sx={ statusStyles }>
            <Grid size={3}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <span >{ "Status: " }</span>
                        <span style={ {  color: (status === 'Online' ? 'lightgreen' : 'red'), marginLeft: '30px'  } }  >{ status }</span>
                        { (problem !== '') &&
                            <Box sx={{ color: 'error.main', fontSize: '0.8rem', marginTop: '4px' }}>
                                { problem }
                            </Box>
                        }
                    </Box>
                    <Box>
                        <IconButton title="Refresh status" onClick={ refreshOperatorStatus }>
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Grid>
            <Grid size={3}>
                { (loading) &&
                    <Box>
                        <span>{ "Connecting to Operator..." }</span>
                    </Box>
                }
                { (!loading) &&
                    <Box>
                        <Button
                            onClick={ handleStartOperator }
                            variant="contained"
                            color="success"
                            disabled={ ( status === 'Online' ) }
                        >
                            <span>{ "Start" }</span>
                        </Button>
                    </Box>
                }
            </Grid>
            <Grid size={3}>
                { (loading) &&
                    <Box>
                        <span>{ "Connecting to Operator..." }</span>
                    </Box>
                }
                { (!loading) &&
                    <Box>
                        <Button
                            onClick={ handleRestartOperator }
                            variant="contained"
                            color="secondary"
                        >
                            <span>{ "Restart" }</span>
                        </Button>
                    </Box>
                }
            </Grid>
            <Grid size={3}>
                { (loading) &&
                    <Box>
                        <span>{ "Connecting to Operator..." }</span>
                    </Box>
                }
                { (!loading) &&
                    <Box>
                        <Button
                            onClick={ handleStopOperator }
                            variant="contained"
                            color="error"
                        >
                            <span>{ "Stop" }</span>
                        </Button>
                    </Box>
                }
            </Grid>
        </Grid>
    )
}